import { useCallback, useEffect, useRef } from 'react'

const GOOGLE_APP_ID = process.env.GOOGLE_APP_ID || ''

// Detect if running in a workspace environment
// Workspace URLs follow the pattern: {workspace-id}.mission-control-web.local.hellolanding.com
function isWorkspaceEnvironment(): boolean {
  return /^[a-z0-9-]+\.mission-control-web\.local\.hellolanding\.com$/.test(window.location.hostname)
}

function getWorkspaceId(): string | null {
  return isWorkspaceEnvironment() ? window.location.hostname.split('.')[0] : null
}

interface UseGoogleSignInProps {
  onError(error: string): void
  onSuccess(res: { credential: string }): void
}

export function useGoogleSignin({ onSuccess }: UseGoogleSignInProps) {
  const googleLoginRef = useRef<{ invokeGoogleAuth(): void } | null>(null)

  const initializeGoogleSignIn = useCallback(() => {
    google.accounts.id.initialize({
      client_id: GOOGLE_APP_ID,
      callback: onSuccess,
    })

    // * render the official google.accounts login button to the DOM
    // * however, render it within a hidden <div> because we want to use our own custom button UI
    const googleLoginWrapper = document.createElement('div')
    googleLoginWrapper.style.display = 'none'
    document.body.appendChild(googleLoginWrapper)
    google.accounts.id.renderButton(googleLoginWrapper, { type: 'standard' })

    // * find the button added by google.accounts that triggers the google OAuth popup
    // * update our ref so that when we want our custom UI button to invoke google Oauth, it will trigger a click on the actual button, thus opening the OAuth popup
    const googleAuthLoginButton = googleLoginWrapper.querySelector('div[role=button]') as HTMLElement
    googleLoginRef.current = {
      invokeGoogleAuth: () => googleAuthLoginButton?.click(),
    }
  }, [onSuccess])

  useEffect(() => {
    // Skip loading Google GIS in workspace environments — OAuth is handled by the auth proxy
    if (isWorkspaceEnvironment()) return

    const scriptTag = document.createElement('script')
    scriptTag.src = 'https://accounts.google.com/gsi/client'
    scriptTag.async = true
    scriptTag.defer = true
    scriptTag.onload = initializeGoogleSignIn // * onLoad of the gsi client library, we need to initialize by rendering the google login button
    document.body.appendChild(scriptTag)

    return () => {
      document.body.removeChild(scriptTag)
    }
  }, [initializeGoogleSignIn])

  function signInGoogle() {
    const workspaceId = getWorkspaceId()
    if (workspaceId) {
      // Redirect to auth service instead of invoking Google GIS directly
      const pathname = window.location.pathname || '/'
      const safePath = pathname.startsWith('/oauth/') ? '/' : pathname
      const returnPath = encodeURIComponent(safePath)
      window.location.href = `/oauth/google/start?workspace_id=${workspaceId}&service=mission-control-web&return_path=${returnPath}`
      return
    }

    googleLoginRef.current?.invokeGoogleAuth()
  }

  return { signInGoogle }
}
